import { Divider } from '@heroui/react';
import React, { useEffect, useState } from 'react';
import { AdditionalFilters } from '../../components/additional-filters/AdditionalFilters';
import {
	IAdditionalFilter,
	IDataField,
	IDataOption,
	IFileInfo,
	IPortCallsState,
	IVoyagesState,
	ReportType,
} from '../../types/reportsTypes';
import { useAppDispatch, useAppSelector } from '../../../../core/rootHooks';
import { selectDateRangeBoundaries } from '../../selectors/downloadSelectors';
import { CalendarDate } from '@internationalized/date';
import { DataSelection } from '../../components/data-selection/DataSelection';
import { DataPreview } from '../../components/data-preview/DataPreview';
import { selectSelectedVesselsImoNumbers, selectSelectedVesselsSectors } from '../../../../shared/sharedSelectors';
import { selectGeneralSeaAreas } from '../../slices/generalSeaAreasSlice';
import { areaSignalStatisticsActions, selectAreaSignalStatistics } from '../../slices/areaSignalStatisticsSlice';
import { formatDateAsString } from '../../../../utils/helpers';
import { calculateDataExportEstimation } from '../../utils/downloadHelpers';
import { TReportRequestParams } from '../../types/downloadTypes';
import { activityReportActions } from '../../slices/activityReportSlice';
import { addSeascapeLogActivity } from '../../../../activity-logging/activityLogApi';
import { SeascapeActivityLogConstants } from '../../../../activity-logging/activityLogTypes';

const Voyages: React.FC = () => {
	const dispatch = useAppDispatch();
	const dateFormatOptions: Intl.DateTimeFormatOptions = {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	};

	const { firstDayOfGivenMonthOrLastMonth, lastDayOfGivenMonthOrLastMonth } = useAppSelector(selectDateRangeBoundaries);
	const selectedVesselsImoNumbers = useAppSelector(selectSelectedVesselsImoNumbers);
	const generalSeaAreas = useAppSelector(selectGeneralSeaAreas);
	const areaSignalStatistics = useAppSelector(selectAreaSignalStatistics);
	const selectedVesselsSectors = useAppSelector(selectSelectedVesselsSectors);

	const getInitialState = (): IVoyagesState => ({
		additionalFilters: {
			imoNumbers: [],
			sectors: [],
			originDateRangeAndLocation: {
				dateRange: {
					start: new CalendarDate(
						firstDayOfGivenMonthOrLastMonth.getFullYear(),
						firstDayOfGivenMonthOrLastMonth.getMonth() + 1,
						firstDayOfGivenMonthOrLastMonth.getDate(),
					),
					end: new CalendarDate(
						lastDayOfGivenMonthOrLastMonth.getFullYear(),
						lastDayOfGivenMonthOrLastMonth.getMonth() + 1,
						lastDayOfGivenMonthOrLastMonth.getDate(),
					),
				},
				location: {
					type: 'all',
					coordinates: {
						lat: null,
						lon: null,
						latTo: null,
						lonTo: null,
					},
					areaId: [],
					polygonId: undefined,
				},
			},
			destinationDateRangeAndLocation: {
				dateRange: {
					start: new CalendarDate(
						firstDayOfGivenMonthOrLastMonth.getFullYear(),
						firstDayOfGivenMonthOrLastMonth.getMonth() + 1,
						firstDayOfGivenMonthOrLastMonth.getDate(),
					),
					end: new CalendarDate(
						lastDayOfGivenMonthOrLastMonth.getFullYear(),
						lastDayOfGivenMonthOrLastMonth.getMonth() + 1,
						lastDayOfGivenMonthOrLastMonth.getDate(),
					),
				},
				location: {
					type: 'all',
					coordinates: {
						lat: null,
						lon: null,
						latTo: null,
						lonTo: null,
					},
					areaId: [],
					polygonId: undefined,
				},
			},
		},
		dataFields: [
			{
				id: 'origin_polygon_name',
				label: 'Origin Polygon',
				selected: false,
				columnSizeCsv: 20,
				order: 1,
				category: 'location',
				groupOrder: 1,
			},
			{
				id: 'origin_port_name',
				label: 'Origin Port',
				selected: false,
				columnSizeCsv: 20,
				order: 2,
				category: 'location',
				groupOrder: 1,
			},
			{
				id: 'origin_country',
				label: 'Origin Country',
				selected: false,
				columnSizeCsv: 20,
				order: 3,
				category: 'location',
				groupOrder: 1,
			},			
			{
				id: 'destination_polygon_name',
				label: 'Destination Polygon',
				selected: false,
				columnSizeCsv: 20,
				order: 4,
				category: 'location',
				groupOrder: 1,
			},
			{
				id: 'destination_port_name',
				label: 'Destination Port',
				selected: false,
				columnSizeCsv: 20,
				order: 5,
				category: 'location',
				groupOrder: 1,
			},
			{
				id: 'destination_country',
				label: 'Destination Country',
				selected: false,
				columnSizeCsv: 20,
				order: 6,
				category: 'location',
				groupOrder: 1,
			},
			{
				id: 'origin_start_time',
				label: 'Origin Port Start Time',
				selected: false,
				columnSizeCsv: 25,
				order: 3,
				category: 'time',
				groupOrder: 7,
			},
			{
				id: 'origin_end_time',
				label: 'Origin Port End Time',
				selected: false,
				columnSizeCsv: 25,
				order: 4,
				category: 'time',
				groupOrder: 7,
			},
			{
				id: 'total_in_origin_port',
				label: 'Time in Origin Port',
				selected: false,
				columnSizeCsv: 20,
				order: 5,
				category: 'time',
				groupOrder: 7,
			},
			{
				id: 'origin_port_activity',
				label: 'Origin Port Activity',
				selected: false,
				columnSizeCsv: 20,
				order: 6,
				category: 'activity',
				groupOrder: 8,
			},
			{
				id: 'destination_start_time',
				label: 'Destination Port Start Time',
				selected: false,
				columnSizeCsv: 25,
				order: 9,
				category: 'time',
				groupOrder: 7,
			},
			{
				id: 'destination_end_time',
				label: 'Destination Port End Time',
				selected: false,
				columnSizeCsv: 25,
				order: 10,
				category: 'time',
				groupOrder: 7,
			},
			{
				id: 'total_in_destination_port',
				label: 'Time in Destination Port',
				selected: false,
				columnSizeCsv: 25,
				order: 11,
				category: 'time',
				groupOrder: 7,
			},
			{
				id: 'destination_port_activity',
				label: 'Destination Port Activity',
				selected: false,
				columnSizeCsv: 25,
				order: 12,
				category: 'activity',
				groupOrder: 8,
			},
			{
				id: 'voyage_draught',
				label: 'Voyage Draft',
				selected: false,
				columnSizeCsv: 15,
				order: 13,
				category: 'vessel',
				groupOrder: 4,
			},
			{
				id: 'deadweight_loaded',
				label: 'Deadweight Loaded',
				selected: false,
				columnSizeCsv: 18,
				order: 14,
				category: 'vessel',
				groupOrder: 4,
			},
			{ id: 'laden', label: '% Laden', selected: false, columnSizeCsv: 12, order: 15, category: 'vessel', groupOrder: 4 },
			{
				id: 'voyage_id',
				label: 'Voyage ID',
				selected: false,
				columnSizeCsv: 10,
				order: 16,
				category: 'voyage',
				groupOrder: 5,
			},
			{
				id: 'voyage_status',
				label: 'Voyage Status',
				selected: false,
				columnSizeCsv: 15,
				order: 17,
				category: 'voyage',
				groupOrder: 5,
			},
			{
				id: 'voyage_time',
				label: 'Voyage Time',
				selected: false,
				columnSizeCsv: 15,
				order: 18,
				category: 'voyage',
				groupOrder: 5,
			},
			{
				id: 'voyage_distance',
				label: 'Voyage Distance',
				selected: false,
				columnSizeCsv: 18,
				order: 19,
				category: 'voyage',
				groupOrder: 5,
			},
			{
				id: 'voyage_type',
				label: 'Voyage Type',
				selected: false,
				columnSizeCsv: 15,
				order: 20,
				category: 'voyage',
				groupOrder: 5,
			},
			{
				id: 'fuel_consumed_in_origin_port',
				label: 'Fuel Consumed in Origin Port',
				selected: false,
				columnSizeCsv: 28,
				order: 20,
				category: 'fuel',
				groupOrder: 6,
			},
			{
				id: 'fuel_consumed_on_voyage',
				label: 'Fuel Consumed on Voyage',
				selected: false,
				columnSizeCsv: 25,
				order: 21,
				category: 'fuel',
				groupOrder: 6,
			},
			{
				id: 'fuel_consumed_in_destination_port',
				label: 'Fuel Consumed in Destination Port',
				selected: false,
				columnSizeCsv: 32,
				order: 22,
				category: 'fuel',
				groupOrder: 6,
			},
			{
				id: 'total_fuel_consumed',
				label: 'Total Fuel Consumed',
				selected: false,
				columnSizeCsv: 20,
				order: 23,
				category: 'fuel',
				groupOrder: 6,
			},
			{
				id: 'emission_consumed_in_origin_port',
				label: 'Emissions Consumed in Origin Port',
				selected: false,
				columnSizeCsv: 32,
				order: 24,
				category: 'emissions',
				groupOrder: 2,
			},
			{
				id: 'emission_consumed_in_destination_port',
				label: 'Emissions Consumed in Destination Port',
				selected: false,
				columnSizeCsv: 35,
				order: 25,
				category: 'emissions',
				groupOrder: 2,
			},
			{
				id: 'emission_consumed_on_voyage',
				label: 'Emissions Consumed on Voyage',
				selected: false,
				columnSizeCsv: 28,
				order: 26,
				category: 'emissions',
				groupOrder: 2,
			},
			{
				id: 'total_emission_consumed',
				label: 'Total Emissions Consumed',
				selected: false,
				columnSizeCsv: 25,
				order: 27,
				category: 'emissions',
				groupOrder: 2,
			},
			{
				id: 'eu_ets_qualify_load',
				label: 'EU ETS/FuelEU Qualifying Load',
				selected: false,
				columnSizeCsv: 30,
				order: 28,
				category: 'compliance',
				groupOrder: 3,
			},
			{
				id: 'eu_ets_qualify_voyage',
				label: 'EU ETS/FuelEU Qualifying Voyage',
				selected: false,
				columnSizeCsv: 32,
				order: 29,
				category: 'compliance',
				groupOrder: 3,
			},
			{
				id: 'eu_ets_qualify_discharge',
				label: 'EU ETS/FuelEU Qualifying Discharge',
				selected: false,
				columnSizeCsv: 35,
				order: 30,
				category: 'compliance',
				groupOrder: 3,
			},
		], // Static data fields based on screenshot - will be populated from API later
		fileInfo: {
			recordCount: 0,
			vesselCount: 0,
			firstRecord: '-',
			lastRecord: '-',
			estimatedSize: '0.00 MB',
			fileType: 'parquet',
		},
	});

	const [state, setState] = useState<IPortCallsState>(getInitialState());
	const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false); // TODO: Will be used when implementing API data loading

	const updateVesselData = () => {
		// If location type is latlong, dispatch area signal statistics request
		if (state.additionalFilters.originDateRangeAndLocation!.location!.type === 'latlong' && state.additionalFilters.originDateRangeAndLocation!.location!.coordinates) {
			const { lat, lon, latTo, lonTo } = state.additionalFilters.originDateRangeAndLocation!.location!.coordinates;

			// Ensure all coordinates are provided and are numbers
			if (
				typeof lat === 'number' &&
				typeof lon === 'number' &&
				typeof latTo === 'number' &&
				typeof lonTo === 'number' &&
				lat !== latTo &&
				lon !== lonTo &&
				!isNaN(lat) &&
				!isNaN(lon) &&
				!isNaN(latTo) &&
				!isNaN(lonTo)
			) {
				// Dispatch area
				dispatch(
					areaSignalStatisticsActions.getAreaSignalStatisticsRequestAction({
						lat,
						latTo,
						lon,
						lonTo,
					}),
				);
			} else {
				return;
			}
		}

		const adjustedVesselCount = calculateAdjustedVesselCount();
		const exportEstimation = calculateEstimationWithAdjustments(adjustedVesselCount);

		setState((prev) => ({
			...prev,
			additionalFilters: {
				...prev.additionalFilters,
				imoNumbers: selectedVesselsImoNumbers,
				sectors: selectedVesselsSectors,
			},
			fileInfo: {
				...prev.fileInfo,
				vesselCount: selectedVesselsImoNumbers.length,
				firstRecord: formatDateAsString(state.additionalFilters.originDateRangeAndLocation!.dateRange!.start?.toString(), dateFormatOptions) ?? '-',
				lastRecord: formatDateAsString(state.additionalFilters.destinationDateRangeAndLocation!.dateRange!.end?.toString(), dateFormatOptions) ?? '-',
				estimatedSize: exportEstimation.estimatedSize,
				recordCount: exportEstimation.estimatedRecords,
			},
		}));
	};

	const handleLoadData = () => {
		updateVesselData();
		setIsDataLoaded(true);
	};

	// Set up event listener for the Load button
	useEffect(() => {
		const handleLoadEvent = () => {
			handleLoadData();
		};

		window.addEventListener('activity-report:load', handleLoadEvent);

		return () => {
			window.removeEventListener('activity-report:load', handleLoadEvent);
		};
	}, [state]); // Re-add event listener when state changes to capture latest state

	// Handler functions for state updates
	const handleAdditionalFiltersChange = (filters: IAdditionalFilter) => {
		if (filters.originDateRangeAndLocation!.location!.type === 'latlong' && !filters.originDateRangeAndLocation!.location!.coordinates) {
			// If location type is latlong but no coordinates are provided, reset the coordinates
			filters.originDateRangeAndLocation!.location!.coordinates = {
				lat: -90,
				latTo: 90,
				lon: -180,
				lonTo: 180,
			};
		}

		if (filters.destinationDateRangeAndLocation!.location!.type === 'latlong' && !filters.destinationDateRangeAndLocation!.location!.coordinates) {
			// If location type is latlong but no coordinates are provided, reset the coordinates
			filters.destinationDateRangeAndLocation!.location!.coordinates = {
				lat: -90,
				latTo: 90,
				lon: -180,
				lonTo: 180,
			};
		}

		setState((prev) => ({
			...prev,
			additionalFilters: filters,
		}));
		setIsDataLoaded(false); // Reset data loaded state when filters change
	};

	const handleDataFieldsChange = (dataFields: IDataField[]) => {
		setState((prev) => ({
			...prev,
			dataFields,
		}));
		setIsDataLoaded(false); // Reset data loaded state when data fields change
	};

	const calculateAdjustedVesselCount = (locationOverride?: IAdditionalFilter['location']): number => {
		let adjustedVesselCount = selectedVesselsImoNumbers.length;
		const location = locationOverride || state.additionalFilters.originDateRangeAndLocation!.location;

		// If location type is seaArea, filter vessel count according to signalPercent
		if (location!.type === 'seaArea' && location!.areaId && generalSeaAreas) {
			const selectedAreaIds = location!.areaId;

			// Find the selected sea areas and calculate sum of signal percentages
			const selectedSeaAreas = generalSeaAreas.filter((area) => selectedAreaIds.includes(area.name));

			if (selectedSeaAreas.length > 0) {
				// Calculate sum of signal percentages across selected areas
				const totalSignalPercent = selectedSeaAreas.reduce((sum, area) => sum + area.signalPercent, 0);

				// Calculate adjusted vessel count based on the sum of signal percentages
				adjustedVesselCount = Math.ceil(selectedVesselsImoNumbers.length * (totalSignalPercent / 100));
			}
		} else if (location!.type === 'latlong' && areaSignalStatistics && areaSignalStatistics.percent) {
			// For latlong, we'll use the response from area signal statistics (handled in useEffect)
			adjustedVesselCount = Math.ceil(selectedVesselsImoNumbers.length * (areaSignalStatistics.percent / 100));
		}

		return adjustedVesselCount * 1.2;
	};

	const calculateEstimationWithAdjustments = (
		overrideVesselCount?: number,
		dateRangeOverride?: IAdditionalFilter['dateRange'],
		dataFieldsOverride?: IDataField[],
		frequencyOverride?: IDataOption['frequency'],
		fileTypeOverride?: string,
		locationOverride?: IAdditionalFilter['location'],
	): ReturnType<typeof calculateDataExportEstimation> => {
		const vesselCount = overrideVesselCount ?? calculateAdjustedVesselCount(locationOverride);
		return calculateDataExportEstimation(
			vesselCount,
			dataFieldsOverride || state.dataFields,
			dateRangeOverride || state.additionalFilters.originDateRangeAndLocation!.dateRange!,
			frequencyOverride || 'monthly',
			fileTypeOverride || state.fileInfo.fileType,
		);
	};

	const handleFileInfoChange = (fileInfo: IFileInfo) => {
		setState((prev) => ({
			...prev,
			fileInfo,
		}));

		// If location type is latlong, dispatch area signal statistics request to ensure we have updated data
		if (state.additionalFilters.originDateRangeAndLocation!.location!.type === 'latlong' && state.additionalFilters.originDateRangeAndLocation!.location!.coordinates) {
			const { lat, lon, latTo, lonTo } = state.additionalFilters.originDateRangeAndLocation!.location!.coordinates;

			// Ensure all coordinates are provided and are numbers
			if (
				typeof lat === 'number' &&
				typeof lon === 'number' &&
				typeof latTo === 'number' &&
				typeof lonTo === 'number' &&
				lat !== latTo &&
				lon !== lonTo &&
				!isNaN(lat) &&
				!isNaN(lon) &&
				!isNaN(latTo) &&
				!isNaN(lonTo)
			) {
				// Dispatch area signal statistics request
				dispatch(
					areaSignalStatisticsActions.getAreaSignalStatisticsRequestAction({
						lat,
						latTo,
						lon,
						lonTo,
					}),
				);
			}
		}

		const adjustedVesselCount = calculateAdjustedVesselCount();
		const exportEstimation = calculateEstimationWithAdjustments(
			adjustedVesselCount, // use calculated vessel count
			undefined, // use current date range
			undefined, // use current data fields
			undefined, // use current frequency
			fileInfo.fileType,
		);

		setState((prev) => ({
			...prev,
			fileInfo: {
				...fileInfo,
				estimatedSize: exportEstimation.estimatedSize,
				recordCount: exportEstimation.estimatedRecords,
			},
		}));
	};

	const handleDownload = () => {
		// Create activity report request params from the current state
		// Set end date to end of day (23:59:59) for proper range coverage
		const originEndDate = state.additionalFilters.originDateRangeAndLocation!.dateRange!.end.toDate('UTC');
		originEndDate.setUTCHours(23, 59, 59, 999);

		const destinationEndDate = state.additionalFilters.destinationDateRangeAndLocation!.dateRange!.end.toDate('UTC');
		destinationEndDate.setUTCHours(23, 59, 59, 999);

		const requestParams: TReportRequestParams = {
			reportType: ReportType.Voyages,
			imoList: state.additionalFilters.imoNumbers,
			sectors: state.additionalFilters.sectors!,
			requestedColumns: state.dataFields.filter((field) => field.selected).map((field) => field.id),

			originStartDate: state.additionalFilters.originDateRangeAndLocation!.dateRange!.start.toDate('UTC').toISOString() || '',
			originEndDate: originEndDate.toISOString() || '',
			originMinLat:
				(state.additionalFilters.originDateRangeAndLocation!.location!.type === 'latlong' && (state.additionalFilters.originDateRangeAndLocation!.location!.coordinates?.lat || -90)) ||
				null,
			originMaxLat:
				(state.additionalFilters.originDateRangeAndLocation!.location!.type === 'latlong' && (state.additionalFilters.originDateRangeAndLocation!.location!.coordinates?.latTo || 90)) ||
				null,
			originMinLong:
				(state.additionalFilters.originDateRangeAndLocation!.location!.type === 'latlong' && (state.additionalFilters.originDateRangeAndLocation!.location!.coordinates?.lon || -180)) ||
				null,
			originMaxLong:
				(state.additionalFilters.originDateRangeAndLocation!.location!.type === 'latlong' && (state.additionalFilters.originDateRangeAndLocation!.location!.coordinates?.lonTo || 180)) ||
				null,
			originSeaArea:
				(state.additionalFilters.originDateRangeAndLocation!.location!.type === 'seaArea' &&
					state.additionalFilters.originDateRangeAndLocation!.location!.areaId?.length &&
					state.additionalFilters.originDateRangeAndLocation!.location!.areaId[0]) ||
				'',
			originPolygonIds: (() => {
				const polygonIds: string[] = [];

				// Add polygon IDs from direct polygon selection
				if (state.additionalFilters.originDateRangeAndLocation!.location!.type === 'polygon' && state.additionalFilters.originDateRangeAndLocation!.location!.polygonId?.length) {
					polygonIds.push(...state.additionalFilters.originDateRangeAndLocation!.location!.polygonId);
				}

				// Add polygon IDs from port selection
				if (state.additionalFilters.originDateRangeAndLocation!.location!.type === 'port' && state.additionalFilters.originDateRangeAndLocation!.location!.portPolygonIds?.length) {
					polygonIds.push(...state.additionalFilters.originDateRangeAndLocation!.location!.portPolygonIds);
				}

				return polygonIds;
			})(),

			destinationStartDate: state.additionalFilters.destinationDateRangeAndLocation!.dateRange!.start.toDate('UTC').toISOString() || '',
			destinationEndDate: destinationEndDate.toISOString() || '',
			destinationMinLat:
				(state.additionalFilters.destinationDateRangeAndLocation!.location!.type === 'latlong' && (state.additionalFilters.destinationDateRangeAndLocation!.location!.coordinates?.lat || -90)) ||
				null,
			destinationMaxLat:
				(state.additionalFilters.destinationDateRangeAndLocation!.location!.type === 'latlong' && (state.additionalFilters.destinationDateRangeAndLocation!.location!.coordinates?.latTo || 90)) ||
				null,
			destinationMinLong:
				(state.additionalFilters.destinationDateRangeAndLocation!.location!.type === 'latlong' && (state.additionalFilters.destinationDateRangeAndLocation!.location!.coordinates?.lon || -180)) ||
				null,
			destinationMaxLong:
				(state.additionalFilters.destinationDateRangeAndLocation!.location!.type === 'latlong' && (state.additionalFilters.destinationDateRangeAndLocation!.location!.coordinates?.lonTo || 180)) ||
				null,
			destinationSeaArea:
				(state.additionalFilters.destinationDateRangeAndLocation!.location!.type === 'seaArea' &&
					state.additionalFilters.destinationDateRangeAndLocation!.location!.areaId?.length &&
					state.additionalFilters.destinationDateRangeAndLocation!.location!.areaId[0]) ||
				'',
			destinationPolygonIds: (() => {
				const polygonIds: string[] = [];

				// Add polygon IDs from direct polygon selection
				if (state.additionalFilters.destinationDateRangeAndLocation!.location!.type === 'polygon' && state.additionalFilters.destinationDateRangeAndLocation!.location!.polygonId?.length) {
					polygonIds.push(...state.additionalFilters.destinationDateRangeAndLocation!.location!.polygonId);
				}

				// Add polygon IDs from port selection
				if (state.additionalFilters.destinationDateRangeAndLocation!.location!.type === 'port' && state.additionalFilters.destinationDateRangeAndLocation!.location!.portPolygonIds?.length) {
					polygonIds.push(...state.additionalFilters.destinationDateRangeAndLocation!.location!.portPolygonIds);
				}

				return polygonIds;
			})(),

			fileType: state.fileInfo.fileType,
		};

		const payload = JSON.stringify({ DataType: 'Voyages', RequestedParams: requestParams });

		addSeascapeLogActivity({ eventId: SeascapeActivityLogConstants.MSI_DATA_DOWNLOAD, payload });

		// Dispatch the request action
		dispatch(activityReportActions.submitActivityReportRequestAction(requestParams));
	};

	return (
		<div className="voyages-report-container flex w-full h-full gap-4">
			{/* Left panel: Additional Filters */}
			<div className="w-2/3 h-full">
				<AdditionalFilters
					filters={state.additionalFilters}
					onChange={handleAdditionalFiltersChange}
					maxDate={lastDayOfGivenMonthOrLastMonth}
					reportType={ReportType.Voyages}
				/>
			</div>

			<Divider orientation="vertical" />

			{/* Middle panel: Data Selection */}
			<div className="w-full h-full">
				<DataSelection dataFields={state.dataFields} onDataFieldsChange={handleDataFieldsChange} />
			</div>

			<Divider orientation="vertical" />

			{/* Right panel: Data Preview */}
			<div className="w-2/3 h-full">
				<DataPreview
					fileInfo={state.fileInfo}
					onFileInfoChange={handleFileInfoChange}
					isLoading={false}
					onDownload={handleDownload}
					isDataLoaded={isDataLoaded}
				/>
			</div>
		</div>
	);
};

export default Voyages;
